import React, { useCallback } from 'react';
import * as Haptics from 'expo-haptics';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { theme } from '@garnish/constants';
import { Button, VStack } from '@garnish/core';

import { PermissionsPrompt } from '@sg/shared-fe-features-permissions';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPrimerPushView = (props: Props) => {
  const { isRequesting, isSkipping, onAllow, onSkip } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  // Android allows a "Not now" button, while it is against Apple's guidelines on iOS.
  const isSkipAvailable = Platform.OS !== 'ios';

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleAllow = useCallback(() => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onAllow();
  }, [onAllow]);

  const handleSkip = useCallback(() => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onSkip();
  }, [onSkip]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <PermissionsPrompt.Title>
        <FormattedMessage {...messages.title} />
      </PermissionsPrompt.Title>

      <PermissionsPrompt.Text>
        <FormattedMessage {...messages.text} />
      </PermissionsPrompt.Text>

      <VStack gap={theme.spacing['4']}>
        {isSkipAvailable ? (
          <Button
            onPress={handleSkip}
            palette="cream"
            size="large-wide"
            isLoading={isSkipping}
            disabled={isRequesting}
          >
            {formatMessage(messages.notNow)}
          </Button>
        ) : null}

        <Button
          onPress={handleAllow}
          palette="oatmeal"
          size="large-wide"
          disabled={isSkipping}
          isLoading={isRequesting}
        >
          {formatMessage(messages.allow)}
        </Button>
      </VStack>
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Get the most out of our app…',
    description: 'Permissions Primer | Push | Title',
  },
  text: {
    defaultMessage:
      'When you turn on push notifications, you’ll get surprise offers, menu updates and more.',
    description: 'Permissions Primer | Push | Body text',
  },
  notNow: {
    defaultMessage: 'Not now',
    description: 'Permissions Primer | Push | Skip CTA',
  },
  allow: {
    defaultMessage: 'Continue',
    description: 'Permissions Primer | Push | Allow CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isRequesting: boolean;
  isSkipping: boolean;
  onAllow: () => void;
  onSkip: () => void;
};
